import { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes, faPaperPlane, faChevronLeft, faImage } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../AuthContext';
import Modal from '../components/Modal';
import './Tickets.css';

const API = process.env.REACT_APP_API_URL || 'https://api.bustisha.com/api';

const priorityColors = { high: 'priority-high', medium: 'priority-medium', low: 'priority-low' };
const statusColors = { pending: 'status-pending', replied: 'status-processing', closed: 'status-completed' };

export default function Tickets() {
  const { token } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ subject: '', message: '', priority: 'medium' });
  const [formFile, setFormFile] = useState(null);
  const [reply, setReply] = useState('');
  const [replyFile, setReplyFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [modal, setModal] = useState({ open: false, type: 'info', title: '', message: '' });
  const formFileRef = useRef(null);
  const replyFileRef = useRef(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchTickets = async () => {
    const res = await fetch(`${API}/tickets`, { headers: authHeaders });
    const data = await res.json();
    setTickets(Array.isArray(data) ? data : []);
  };

  useEffect(() => { fetchTickets(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const buildFormData = (data, file) => {
    const fd = new FormData();
    Object.entries(data).forEach(([k, v]) => fd.append(k, v));
    if (file) fd.append('attachment', file);
    return fd;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) return;
    setSending(true);
    const res = await fetch(`${API}/tickets`, {
      method: 'POST',
      headers: authHeaders,
      body: buildFormData(form, formFile),
    });
    const data = await res.json();
    setSending(false);
    if (!res.ok) { setModal({ open: true, type: 'error', title: 'Failed', message: data.message || 'Could not create ticket.' }); return; }
    setForm({ subject: '', message: '', priority: 'medium' });
    setFormFile(null);
    setShowNew(false);
    fetchTickets();
    setModal({ open: true, type: 'success', title: 'Ticket Created', message: 'Your ticket has been submitted. We will get back to you soon.' });
  };

  const openTicket = async (t) => {
    const res = await fetch(`${API}/tickets/${t.id}`, { headers: authHeaders });
    const data = await res.json();
    if (data.id) setSelected(data);
  };

  const handleReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    const res = await fetch(`${API}/tickets/${selected.id}/reply`, {
      method: 'POST',
      headers: authHeaders,
      body: buildFormData({ message: reply }, replyFile),
    });
    const data = await res.json();
    setSending(false);
    if (!res.ok) { setModal({ open: true, type: 'error', title: 'Reply Failed', message: data.message || 'Could not send reply.' }); return; }
    setReply('');
    setReplyFile(null);
    if (replyFileRef.current) replyFileRef.current.value = '';
    openTicket(selected);
  };

  const AttachmentDisplay = ({ reply: r }) => {
    if (!r.attachment) return null;
    const url = `${API.replace('/api', '')}/storage/${r.attachment}`;
    return (
      <div className="msg-attachment">
        <a href={url} target="_blank" rel="noopener noreferrer">
          <img src={url} alt="Payment screenshot" className="msg-attachment-img" />
        </a>
      </div>
    );
  };

  return (
    <div className="tickets-page">
      <div className="tickets-header">
        {!selected && (
          <button className="new-ticket-btn" onClick={() => setShowNew(!showNew)}>
            <FontAwesomeIcon icon={showNew ? faTimes : faPlus} />
            <span>{showNew ? 'Cancel' : 'New Ticket'}</span>
          </button>
        )}
        {selected && (
          <button className="new-ticket-btn" onClick={() => { setSelected(null); setReply(''); setReplyFile(null); }}>
            <FontAwesomeIcon icon={faChevronLeft} />
            <span>Back</span>
          </button>
        )}
      </div>

      {showNew && !selected && (
        <div className="new-ticket-form">
          <h2 className="section-title">Create New Ticket</h2>
          <form onSubmit={handleCreate}>
            <div className="form-group">
              <label className="form-label">Subject</label>
              <input type="text" className="form-input" placeholder="Brief description of your issue" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Priority</label>
              <select className="form-input" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Message</label>
              <textarea className="form-input form-textarea" placeholder="Describe your issue in detail..." rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label file-input-label">
                <FontAwesomeIcon icon={faImage} /> {formFile ? formFile.name : 'Attach payment screenshot (optional)'}
              </label>
              <input type="file" ref={formFileRef} accept="image/*" className="file-input" onChange={(e) => setFormFile(e.target.files[0])} />
            </div>
            <button type="submit" className="submit-btn" disabled={sending}>{sending ? 'Submitting...' : 'Submit Ticket'}</button>
          </form>
        </div>
      )}

      {selected ? (
        <div className="ticket-detail">
          <div className="ticket-detail-header">
            <h2 className="ticket-detail-subject">{selected.subject}</h2>
            <div className="ticket-detail-meta">
              <span className={`status-badge ${statusColors[selected.status]}`}>{selected.status}</span>
              <span className={`priority-badge ${priorityColors[selected.priority]}`}>{selected.priority}</span>
              <span className="ticket-detail-date">{new Date(selected.created_at).toLocaleDateString()}</span>
            </div>
          </div>

          <div className="ticket-messages">
            <div className="ticket-msg user-msg">
              <div className="msg-header">
                <span className="msg-author">You</span>
                <span className="msg-date">{new Date(selected.created_at).toLocaleDateString()}</span>
              </div>
              <p className="msg-body">{selected.message}</p>
            </div>
            {(selected.replies || []).map((r) => (
              <div key={r.id} className={`ticket-msg ${r.user?.role === 'admin' ? 'admin-msg' : 'user-msg'}`}>
                <div className="msg-header">
                  <span className="msg-author">{r.user?.role === 'admin' ? 'Support' : 'You'}</span>
                  <span className="msg-date">{new Date(r.created_at).toLocaleDateString()}</span>
                </div>
                <p className="msg-body">{r.message}</p>
                <AttachmentDisplay reply={r} />
              </div>
            ))}
          </div>

          {selected.status !== 'closed' && (
            <div className="ticket-reply-box">
              <textarea className="form-input reply-textarea" placeholder="Type your reply..." rows={3} value={reply} onChange={(e) => setReply(e.target.value)} />
              <div className="reply-attach-row">
                <label className="reply-attach-label">
                  <FontAwesomeIcon icon={faImage} />
                  <input type="file" ref={replyFileRef} accept="image/*" className="file-input" onChange={(e) => setReplyFile(e.target.files[0])} />
                </label>
                {replyFile && <span className="reply-file-name">{replyFile.name}</span>}
                <button className="reply-btn" onClick={handleReply} disabled={sending}>
                  <FontAwesomeIcon icon={faPaperPlane} />
                  <span>{sending ? 'Sending...' : 'Send Reply'}</span>
                </button>
              </div>
            </div>
          )}
          {selected.status === 'closed' && (
            <p className="ticket-closed-notice">This ticket is closed. Create a new ticket for further assistance.</p>
          )}
        </div>
      ) : (
        <div className="tickets-list">
          {tickets.length === 0 && <p className="tickets-empty">No tickets yet.</p>}
          {tickets.map((t) => (
            <div key={t.id} className="ticket-card" onClick={() => openTicket(t)} style={{ cursor: 'pointer' }}>
              <div className="ticket-main">
                <div className="ticket-info">
                  <span className="ticket-id">#{t.id}</span>
                  <span className="ticket-subject">{t.subject}</span>
                </div>
                <div className="ticket-meta">
                  <span className={`status-badge ${statusColors[t.status]}`}>{t.status}</span>
                  <span className={`priority-badge ${priorityColors[t.priority]}`}>{t.priority}</span>
                  <span className="ticket-date">{new Date(t.created_at).toLocaleDateString()}</span>
                  <span className="ticket-replies">{t.replies?.length || 0} replies</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={modal.open} type={modal.type} title={modal.title} message={modal.message} onClose={() => setModal({ ...modal, open: false })} />
    </div>
  );
}
